import { ElementRef, Injector, WritableSignal, afterNextRender, effect, inject } from '@angular/core';

// Drives a traveling underline from the snap container's fractional scroll position, so it
// follows panel swipes continuously instead of jumping only on tab flip.
export function updateSnapTabIndicator(
  snap: HTMLElement | undefined,
  tabs: readonly ElementRef<HTMLElement>[],
  indicator: HTMLElement | undefined,
): void {
  if (!snap || !tabs.length || !indicator) return;
  const w = snap.clientWidth;
  if (!w) return;
  const last = tabs.length - 1;
  const frac = Math.max(0, Math.min(last, snap.scrollLeft / w));
  const lo = Math.floor(frac);
  const hi = Math.min(last, lo + 1);
  const t = frac - lo;
  const a = tabs[lo].nativeElement;
  const b = tabs[hi].nativeElement;
  const left = a.offsetLeft + (b.offsetLeft - a.offsetLeft) * t;
  const width = a.offsetWidth + (b.offsetWidth - a.offsetWidth) * t;
  indicator.style.transform = `translateX(${left}px)`;
  indicator.style.width = `${width}px`;
}

/** Wires a snap-scrolling tab strip to its traveling underline: indicator placement on
 *  tab-ref changes, scrollTo on tab select, scroll→active-tab sync. Construct in an
 *  injection context (field initializer / constructor). */
export function createSnapTabStrip<T extends string>(opts: {
  tabIds: readonly T[];
  activeTab: WritableSignal<T>;
  snap: () => HTMLElement | undefined;
  tabs: () => readonly ElementRef<HTMLElement>[];
  indicator: () => HTMLElement | undefined;
}) {
  const injector = inject(Injector);
  const update = () => updateSnapTabIndicator(opts.snap(), opts.tabs(), opts.indicator());
  // Place the indicator after first render, and re-place whenever the tab
  // refs change (e.g. unpinning chat re-mounts the strip).
  effect(() => {
    opts.tabs();
    afterNextRender(update, { injector });
  });
  return {
    update,
    setActiveTab(tab: T): void {
      opts.activeTab.set(tab);
      const idx = opts.tabIds.indexOf(tab);
      const el = opts.snap();
      if (el) el.scrollTo({ left: idx * el.clientWidth, behavior: 'smooth' });
    },
    onScroll(event: Event): void {
      const el = event.target as HTMLElement;
      if (!el.clientWidth) return;
      const idx = Math.round(el.scrollLeft / el.clientWidth);
      const tab = opts.tabIds[idx] ?? opts.tabIds[0];
      if (opts.activeTab() !== tab) opts.activeTab.set(tab);
      update();
    },
  };
}
